import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  format,
  parseISO,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  isValid,
  addDays,
  setHours,
  setMinutes,
  startOfDay,
} from "date-fns";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
} from "@/components/ui/full-calendar/calendar";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle } from "lucide-react";
import {
  FacilitySchedule,
  FacilityScheduleSlot,
} from "@/types/facility/schedule";
import { useGetAllFacilityScheduleSlotQuery } from "@/services/faciltiesApi";
import { Control, FieldErrors, useFormContext, UseFormWatch } from "react-hook-form";

// Extend CalendarEvent type for our use case
interface FacilitySlotEvent extends CalendarEvent {
  is_exclusive?: boolean;
  is_consumed?: boolean;
  originalData?: FacilityScheduleSlot;
}

interface SlotAndScheduleProps {
  control?: Control<FacilitySchedule>;
  errors?: FieldErrors<FacilitySchedule>;
  watch?: UseFormWatch<FacilitySchedule>;
  open: boolean;
}

const SlotsandSchedule = ({ control, open }: SlotAndScheduleProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const form = useFormContext<FacilitySchedule>();
  const { watch, setError, clearErrors } = form;
  const watcher = watch();

  // Watch form data reactively from previous steps
  const facilityId = watch("general_info.facility_id");
  const startDate = watch("general_info.start_date") as string;
  const endDate = watch("general_info.end_date") as string;
  const alwaysAvailable = watch("general_info.always_available");
  const durationPerSlot = watch("general_info.duration_per_slot");
  const breakDuration = watch("general_info.break_duration") as number | undefined;
  const availability = watch("general_info.availability") as any;

  // Get current schedule ID for filtering (in edit mode)
  const currentScheduleId = watch("general_info.id");

  // State for current month view
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (startDate && startDate !== "") {
      try {
        const date = new Date(startDate);
        // Ensure we're using a valid date
        if (!isNaN(date.getTime())) {
          return date;
        }
      } catch (error) {
        console.warn("Invalid start date:", startDate, error);
      }
    }
    return new Date();
  });

  const [selectedSlot, setSelectedSlot] = useState<FacilitySlotEvent | null>(
    null
  );
  const [isViewSlotOpen, setIsViewSlotOpen] = useState(false);

  // Generate query string for API call with date range
  const generateQueryString = useCallback(
    (dateRange: { start: Date; end: Date }) => {
      const params = new URLSearchParams();
      params.append("facility_id", facilityId?.toString() || "");
      params.append("start_date", format(dateRange.start, "yyyy-MM-dd"));
      params.append("end_date", format(dateRange.end, "yyyy-MM-dd"));
      return params.toString();
    },
    [facilityId]
  );

  // Get date range for API call - use form dates if available, otherwise current month
  const apiDateRange = useMemo(() => {
    if (alwaysAvailable) {
      // If always available, use current month range
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      return { start: monthStart, end: monthEnd };
    } else if (startDate && endDate) {
      // Use the specific date range from the form
      return { start: new Date(startDate), end: new Date(endDate) };
    } else if (startDate) {
      // If only start date, use start date to end of month
      const start = new Date(startDate);
      const end = endOfMonth(start);
      return { start, end };
    } else {
      // Fallback to current month
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      return { start: monthStart, end: monthEnd };
    }
  }, [currentDate, startDate, endDate, alwaysAvailable]);

  // Fetch existing facility schedule slots with date range
  const { data: existingSlots, isLoading: isLoadingSlots } =
    useGetAllFacilityScheduleSlotQuery(
      {
        query: generateQueryString(apiDateRange) + "&load_consumed_slots=true",
        orgId: orgId,
      },
      {
        skip: !facilityId || !orgId,
      }
    );

  // Update current date when start date changes
  useEffect(() => {
    if (startDate && startDate !== "") {
      try {
        const date = new Date(startDate);
        if (!isNaN(date.getTime())) {
          setCurrentDate(date);
        }
      } catch (error) {
        console.warn("Invalid start date in useEffect:", startDate, error);
      }
    } else {
      // If no start date, use current date
      setCurrentDate(new Date());
    }
  }, [startDate]);

  // Transform API slots to calendar events
  const transformSlotToEvent = (
    slot: FacilityScheduleSlot,
    isCurrentSchedule: boolean = false
  ): FacilitySlotEvent | null => {
    try {
      // Don't skip current schedule slots in edit mode - we want to show them
      // Only skip other schedules' slots if needed

      // Parse the slot date and times
      const slotDate = slot.date;
      const startDateTime = `${slotDate}T${slot.start_time}`;
      const endDateTime = `${slotDate}T${slot.end_time}`;

      const start = parseISO(startDateTime);
      const end = parseISO(endDateTime);

      // Validate the parsed dates
      if (!isValid(start) || !isValid(end)) {
        console.warn("Invalid date found in slot:", slot);
        return null;
      }

      // Check if slot is consumed (booked)
      const isConsumed = slot.consumed_slots && slot.consumed_slots.length > 0;
      const consumedSlot = isConsumed ? slot.consumed_slots?.[0] : null;

      // Determine color based on slot status and whether it's current schedule
      let color = "green"; // Default: available
      let label = "Available";

      if (isConsumed) {
        color = "red";
        label = "Booked";
      } else if (isCurrentSchedule) {
        color = "orange"; // Current schedule slots
        label = "Current Schedule";
      }

      return {
        id: slot.id.toString(),
        title: `${format(start, "HH:mm")} - ${format(end, "HH:mm")}`,
        start: start,
        end: end,
        color: color,
        tags: [
          {
            label: label,
            color: color,
          },
        ],
        user: {
          name: isConsumed
            ? consumedSlot?.member_name || "Booked"
            : isCurrentSchedule
            ? "Current Schedule"
            : "Available",
          avatar: isConsumed ? consumedSlot?.profile_img : undefined,
        },
        description: isConsumed
          ? `Booked by ${consumedSlot?.member_name} - Duration: ${slot.duration_hours}h`
          : isCurrentSchedule
          ? `Current schedule slot - Duration: ${slot.duration_hours}h`
          : `Available slot - Duration: ${slot.duration_hours}h`,
        location: slot.location || "Facility",
        is_exclusive: slot.is_exclusive,
        is_consumed: isConsumed,
        originalData: slot,
      } as FacilitySlotEvent;
    } catch (error) {
      console.warn("Error transforming slot to event:", slot, error);
      return null;
    }
  };

  // Generate slots based on availability and duration from form data
  const generateSlotsFromForm = useMemo(() => {
    console.log("Generating Draft Slots with:", {
      durationPerSlot,
      breakDuration,
      availability: Object.keys(availability || {}),
      facilityId,
      currentDate: format(currentDate, "yyyy-MM-dd"),
      startDate,
      endDate,
      alwaysAvailable,
    });

    if (!durationPerSlot || !availability || !facilityId) {
      console.log("Missing required data for Draft Slots");
      return [];
    }

    const slots: FacilitySlotEvent[] = [];

    // Determine the date range for slot generation
    let rangeStart: Date;
    let rangeEnd: Date;

    if (alwaysAvailable) {
      // If always available, use current month range
      rangeStart = startOfMonth(currentDate);
      rangeEnd = endOfMonth(currentDate);
    } else if (startDate && endDate) {
      // Use the specific date range from the form
      rangeStart = new Date(startDate);
      rangeEnd = new Date(endDate);
    } else if (startDate) {
      // If only start date, use start date to end of month
      rangeStart = new Date(startDate);
      rangeEnd = endOfMonth(new Date(startDate));
    } else {
      // Fallback to current month
      rangeStart = startOfMonth(currentDate);
      rangeEnd = endOfMonth(currentDate);
    }

    console.log("Date range for slot generation:", {
      rangeStart: format(rangeStart, "yyyy-MM-dd EEEE"),
      rangeEnd: format(rangeEnd, "yyyy-MM-dd EEEE"),
      startDate,
      endDate,
      alwaysAvailable,
    });

    // Generate slots for each day in the range
    let currentDay = rangeStart;
    while (currentDay <= rangeEnd) {
      const dayName = format(currentDay, "EEEE").toLowerCase(); // "monday", "tuesday", etc.
      const dayConfig = availability[dayName];

      // Since we're already iterating within the correct date range, just check day availability
      const dayDate = format(currentDay, "yyyy-MM-dd");

      // Debug: Log day processing
      if (dayConfig?.is_available) {
        console.log(`Processing available day: ${dayDate} (${dayName})`, {
          start_time: dayConfig.time_slots.start_time,
          end_time: dayConfig.time_slots.end_time,
        });
      }

      if (
        dayConfig?.is_available &&
        dayConfig.time_slots.start_time &&
        dayConfig.time_slots.end_time
      ) {
        const startTime = dayConfig.time_slots.start_time;
        const endTime = dayConfig.time_slots.end_time;

        // Parse times
        const [startHours, startMinutes] = startTime.split(":").map(Number);
        const [endHours, endMinutes] = endTime.split(":").map(Number);

        let currentMinutes = startHours * 60 + startMinutes;
        let totalEndMinutes = endHours * 60 + endMinutes;
        
        // Check if this is an overnight range (end <= start means next day)
        const isOvernight = totalEndMinutes <= currentMinutes;
        
        // If overnight, adjust end time to be next day (add 24 hours)
        if (isOvernight) {
          totalEndMinutes += 24 * 60; // Add 24 hours for next day
        }
        
        const slotDurationMinutes = durationPerSlot * 60;
        const breakDurationMinutes = breakDuration || 0; // Break duration in minutes (default 0)

        let slotIndex = 0;
        while (currentMinutes + slotDurationMinutes <= totalEndMinutes) {
          // Calculate slot start time (modulo 24 hours to handle overnight)
          const slotStartMinutesOfDay = currentMinutes % (24 * 60);
          const slotStartHours = Math.floor(slotStartMinutesOfDay / 60);
          const slotStartMinutes = slotStartMinutesOfDay % 60;

          // Determine if slot start is on next day
          // For overnight ranges: if currentMinutes >= 1440, we've crossed to next day
          // For same-day ranges: slot start is always on currentDay
          let slotStartDay = currentDay;
          if (currentMinutes >= 24 * 60) {
            slotStartDay = addDays(currentDay, 1);
          }
          
          const slotStart = setMinutes(
            setHours(startOfDay(slotStartDay), slotStartHours),
            slotStartMinutes
          );

          // Calculate slot end time (modulo 24 hours to handle overnight)
          const slotEndTotalMinutes = currentMinutes + slotDurationMinutes;
          const slotEndMinutesOfDay = slotEndTotalMinutes % (24 * 60);
          const slotEndHours = Math.floor(slotEndMinutesOfDay / 60);
          const slotEndMinutesRemainder = slotEndMinutesOfDay % 60;
          
          // Determine if slot end is on next day
          // If end time (in minutes of day) is less than start time (in minutes of day), 
          // or if we've crossed the 24-hour mark, end is on next day
          let slotEndDay = slotStartDay;
          if (slotEndMinutesOfDay < slotStartMinutesOfDay || slotEndTotalMinutes >= 24 * 60) {
            slotEndDay = addDays(slotStartDay, 1);
          }
          
          const slotEnd = setMinutes(
            setHours(startOfDay(slotEndDay), slotEndHours),
            slotEndMinutesRemainder
          );

          // Debug: Log slot creation
          console.log(
            `Creating slot for ${dayDate} (${dayName}): ${format(
              slotStart,
              "HH:mm"
            )} - ${format(slotEnd, "HH:mm")}${isOvernight ? ' (overnight)' : ''}`
          );

          slots.push({
            id: `${currentDay.getTime()}-${slotIndex}`,
            title: `${format(slotStart, "HH:mm")} - ${format(
              slotEnd,
              "HH:mm"
            )}`,
            start: slotStart,
            end: slotEnd,
            color: "blue", // Use blue for form-generated slots
            tags: [
              {
                label: "Draft Slot",
                color: "blue",
              },
            ],
            user: {
              name: "Form Generated",
            },
            description: `Generated from form data - Duration: ${durationPerSlot}h`,
            location: watch("general_info.location") || "Facility",
            is_exclusive: watcher.general_info.is_exclusive,
          } as FacilitySlotEvent);

          // Move to next slot start time: current slot end + break duration
          // Break is only applied between slots (not after the last one)
          currentMinutes += slotDurationMinutes + breakDurationMinutes;
          slotIndex++;
        }
      }

      currentDay = addDays(currentDay, 1);
    }

    console.log("Generated Draft Slots:", slots.length);
    return slots;
  }, [
    currentDate,
    availability,
    durationPerSlot,
    breakDuration,
    startDate,
    endDate,
    alwaysAvailable,
    facilityId,
    watch("general_info.location"),
  ]);

  // Helper function to check if two time slots overlap
  const doSlotsOverlap = (slot1Start: Date, slot1End: Date, slot2Start: Date, slot2End: Date): boolean => {
    return slot1Start < slot2End && slot2Start < slot1End;
  };

  // Helper function to check if two slots are exactly the same
  const areSlotsExactMatch = (slot1Start: Date, slot1End: Date, slot2Start: Date, slot2End: Date): boolean => {
    return slot1Start.getTime() === slot2Start.getTime() && slot1End.getTime() === slot2End.getTime();
  };

  // Helper function to detect overlapping draft slots with existing available slots
  const detectOverlappingSlots = useMemo(() => {
    if (!existingSlots?.data || generateSlotsFromForm.length === 0) {
      return { hasOverlaps: false, overlappingSlots: [] };
    }

    const overlappingSlots: Array<{
      draftSlot: FacilitySlotEvent;
      existingSlot: FacilityScheduleSlot;
      overlapType: 'exact' | 'partial';
    }> = [];

    // Get only available existing slots (not booked or current schedule slots)
    const availableExistingSlots = existingSlots.data.filter(slot => 
      (!slot.consumed_slots || slot.consumed_slots.length === 0) && 
      (!currentScheduleId || slot.facility_schedule_id !== currentScheduleId)
    );

    generateSlotsFromForm.forEach((draftSlot) => {
      const draftStart = draftSlot.start instanceof Date ? draftSlot.start : new Date(draftSlot.start);
      const draftEnd = draftSlot.end instanceof Date ? draftSlot.end : new Date(draftSlot.end);

      availableExistingSlots.forEach((existingSlot) => {
        const existingDate = existingSlot.date;
        const existingStartDateTime = `${existingDate}T${existingSlot.start_time}`;
        const existingEndDateTime = `${existingDate}T${existingSlot.end_time}`;
        
        const existingStart = parseISO(existingStartDateTime);
        const existingEnd = parseISO(existingEndDateTime);

        if (!isValid(existingStart) || !isValid(existingEnd)) return;

        // OVERLAP POLICY: Currently only blocking exact matches
        // TODO: Uncomment the partial overlap section below if you want to block partial overlaps as well
        
        // Check for exact match - ALWAYS BLOCKED
        if (areSlotsExactMatch(draftStart, draftEnd, existingStart, existingEnd)) {
          overlappingSlots.push({
            draftSlot,
            existingSlot,
            overlapType: 'exact'
          });
        }
        
        /* PARTIAL OVERLAP DETECTION - CURRENTLY DISABLED
         * Uncomment this section if you want to also block partial overlaps
         * 
        // Check for partial overlap - CURRENTLY ALLOWED
        else if (doSlotsOverlap(draftStart, draftEnd, existingStart, existingEnd)) {
          overlappingSlots.push({
            draftSlot,
            existingSlot,
            overlapType: 'partial'
          });
        }
        */
      });
    });

    return {
      hasOverlaps: overlappingSlots.length > 0,
      overlappingSlots
    };
  }, [existingSlots?.data, generateSlotsFromForm, currentScheduleId]);

  // Combine API slots and form-generated slots
  const allEvents = useMemo(() => {
    if (!existingSlots?.data) {
      console.log("No existing slots data, showing only Draft Slots");
      return generateSlotsFromForm;
    }

    // Separate current schedule slots from other slots
    const currentScheduleSlots: FacilitySlotEvent[] = [];
    const otherScheduleSlots: FacilitySlotEvent[] = [];

    existingSlots.data.forEach((slot) => {
      const isCurrentSchedule =
        currentScheduleId && slot.facility_schedule_id === currentScheduleId;
      const transformedSlot = transformSlotToEvent(
        slot,
        Boolean(isCurrentSchedule)
      );

      if (transformedSlot) {
        if (isCurrentSchedule) {
          currentScheduleSlots.push(transformedSlot);
        } else {
          otherScheduleSlots.push(transformedSlot);
        }
      }
    });

    // Create a map of overlapping draft slots for quick lookup
    const overlappingDraftSlotIds = new Set(
      detectOverlappingSlots.overlappingSlots.map(overlap => overlap.draftSlot.id)
    );

    // In edit mode, prioritize existing slots over draft slots
    if (currentScheduleId) {
      // Start with current schedule slots and other schedule slots
      const combined: FacilitySlotEvent[] = [...currentScheduleSlots, ...otherScheduleSlots];

      // Add all draft slots, but mark overlapping ones with warning colors
      generateSlotsFromForm.forEach((draftSlot) => {
        const isOverlapping = overlappingDraftSlotIds.has(draftSlot.id);
        
        if (isOverlapping) {
          // Mark as warning/duplicate slot
          const warningSlot = {
            ...draftSlot,
            color: "yellow",
            tags: [
              {
                label: "Duplicate Draft",
                color: "yellow",
              },
            ],
            description: `⚠️ DUPLICATE: This slot exactly matches an existing available slot - Duration: ${durationPerSlot}h`,
          };
          combined.push(warningSlot);
        } else {
          // Check if this draft slot exactly matches any existing slot
          const draftStart = draftSlot.start instanceof Date ? draftSlot.start : new Date(draftSlot.start);
          const draftEnd = draftSlot.end instanceof Date ? draftSlot.end : new Date(draftSlot.end);

          const isExactDuplicate = combined.some((existingSlot) => {
            const existingStart = existingSlot.start instanceof Date ? existingSlot.start : new Date(existingSlot.start);
            const existingEnd = existingSlot.end instanceof Date ? existingSlot.end : new Date(existingSlot.end);
            return areSlotsExactMatch(draftStart, draftEnd, existingStart, existingEnd);
          });

          // Only add draft slot if it doesn't exactly match an existing slot
          if (!isExactDuplicate) {
            combined.push(draftSlot);
          }
        }
      });

      console.log("Combined Events (Edit Mode):", combined.length);
      return combined;
    } else {
      // Create mode: Prefer existing API slots; add draft slots with conflict detection
      const combined = [...otherScheduleSlots];

      // Add form-generated slots (draft) with conflict detection
      generateSlotsFromForm.forEach((draftSlot) => {
        const isOverlapping = overlappingDraftSlotIds.has(draftSlot.id);
        
        if (isOverlapping) {
          // Mark as warning/duplicate slot
          const warningSlot = {
            ...draftSlot,
            color: "yellow",
            tags: [
              {
                label: "Duplicate Draft",
                color: "yellow",
              },
            ],
            description: `⚠️ DUPLICATE: This slot exactly matches an existing available slot - Duration: ${durationPerSlot}h`,
          };
          combined.push(warningSlot);
        } else {
          const draftStart = draftSlot.start instanceof Date ? draftSlot.start : new Date(draftSlot.start);
          const draftEnd = draftSlot.end instanceof Date ? draftSlot.end : new Date(draftSlot.end);

          const isExactDuplicate = combined.some((existingSlot) => {
            const existingStart = existingSlot.start instanceof Date ? existingSlot.start : new Date(existingSlot.start);
            const existingEnd = existingSlot.end instanceof Date ? existingSlot.end : new Date(existingSlot.end);
            return areSlotsExactMatch(draftStart, draftEnd, existingStart, existingEnd);
          });

          if (!isExactDuplicate) {
            combined.push(draftSlot);
          }
        }
      });

      console.log("Combined Events (Create Mode):", combined.length);
      return combined;
    }
  }, [existingSlots?.data, generateSlotsFromForm, currentScheduleId, detectOverlappingSlots, durationPerSlot]);

  // Navigation functions
  const handlePrevious = useCallback(() => {
    console.log("Previous month clicked");
    setCurrentDate((prev) => {
      const newDate = subMonths(prev, 1);
      console.log("New date:", newDate);
      return newDate;
    });
  }, []);

  const handleNext = useCallback(() => {
    console.log("Next month clicked");
    setCurrentDate((prev) => {
      const newDate = addMonths(prev, 1);
      console.log("New date:", newDate);
      return newDate;
    });
  }, []);

  const handleViewSlot = (event: FacilitySlotEvent) => {
    setSelectedSlot(event);
    setIsViewSlotOpen(true);
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // Check if we should show the calendar
  const shouldShowCalendar = facilityId && durationPerSlot;

  // Set form errors when overlaps are detected
  useEffect(() => {
    if (detectOverlappingSlots.hasOverlaps) {
      // Create a detailed error message - currently only exact matches are blocked
      const exactMatches = detectOverlappingSlots.overlappingSlots.filter(overlap => overlap.overlapType === 'exact');
      const errorMessage = `${exactMatches.length} exact slot duplicate(s) detected. Please adjust your availability settings to resolve conflicts.`;
      
      setError("slot_overlaps", {
        type: "manual",
        message: errorMessage,
      });
    } else {
      // Clear the error when no overlaps
      clearErrors("slot_overlaps");
    }
  }, [detectOverlappingSlots, setError, clearErrors]);

  // Get form errors
  const formErrors = form.formState.errors;
  const slotOverlapError = formErrors.slot_overlaps as any;

  console.log({ allEvents, selectedSlot, existingSlots, watcher, formErrors });
  return (
    <div className="space-y-4" data-component="slots-and-schedule">
      <Card className="p-4">
        <p className="font-medium text-base">Slots & Schedule</p>
        <Separator className="h-[1px]" />

        {/* Form Error Display */}
        {/* {slotOverlapError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Form Validation Error
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  {slotOverlapError.message}
                </p>
              </div>
            </div>
          </div>
        )} */}

        {!shouldShowCalendar && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-center text-amber-800">
              <Clock className="h-5 w-5 mr-2" />
              <span>
                Please complete the previous steps to view facility slots
              </span>
            </div>
          </div>
        )}

        {shouldShowCalendar && (
          <>
            {/* Overlap Warning - Only shows for exact matches (duplicates) */}
            {detectOverlappingSlots.hasOverlaps && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Duplicate Slots Detected
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="mb-2">
                        {detectOverlappingSlots.overlappingSlots.filter(overlap => overlap.overlapType === 'exact').length} draft slot(s) exactly match existing available slots:
                      </p>
                      <ul className="list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                        {detectOverlappingSlots.overlappingSlots
                          .filter(overlap => overlap.overlapType === 'exact')
                          .map((overlap, index) => (
                            <li key={index} className="text-xs">
                              <strong>{overlap.draftSlot.title}</strong> on{' '}
                              {format(overlap.draftSlot.start, "EEEE, MMM dd")} is identical to existing slot{' '}
                              <strong>{overlap.existingSlot.start_time} - {overlap.existingSlot.end_time}</strong>
                            </li>
                          ))}
                      </ul>
                      <p className="mt-2 font-medium">
                        Please adjust your availability settings to avoid creating duplicate slots.
                      </p>
                      {/* <p className="mt-1 text-xs">
                        Note: Partial overlaps are currently allowed - only exact duplicates are blocked.
                      </p> */}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center space-x-4 mb-4 p-3 bg-gray-50 rounded-md flex-wrap">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm">Available Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm">Booked Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span className="text-sm">Draft Slots (Preview)</span>
              </div>
              {detectOverlappingSlots.hasOverlaps && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-sm">Duplicate Slots</span>
                </div>
              )}
              {currentScheduleId && (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-sm">Current Schedule</span>
                </div>
              )}
              <div className="text-sm text-gray-600">
                Duration: {durationPerSlot}h per slot
              </div>
            </div>

            {/* Calendar */}
            <div className="relative w-full h-[600px]">
              <FullCalendar
                  events={allEvents}
                  initialView="month"
                  initialDate={currentDate}
                  key={`calendar-${currentDate.getTime()}`} // Force re-render when date changes
                  isCustomView={true}
                  onCreateEvent={undefined} // Disable create event
                  onViewEvent={handleViewSlot}
                  onFilterEvents={undefined} // Disable filter
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  disableNavigation={false}
                />
            </div>

            {/* Slot Details Dialog */}
            <Dialog open={isViewSlotOpen} onOpenChange={setIsViewSlotOpen}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    Slot Details
                  </DialogTitle>
                </DialogHeader>
                {selectedSlot && (
                  <div className="space-y-4">
                    <div className="grid gap-2 ">
                      <div className="flex gap-2 items-center">
                        {selectedSlot.originalData && (
                          <div className="flex gap-2 items-center">
                            <span className="font-medium">Slot ID:</span>
                            <p className="text-sm text-gray-600">
                              {selectedSlot.originalData.id}
                            </p>
                          </div>
                        )}
                        {selectedSlot.is_exclusive && (
                          <div>
                            <span
                              className={cn(
                                "px-2 py-1 rounded text-xs font-medium bg-red-500 text-white"
                              )}
                            >
                              Exclusive
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Time:</span>
                        <span className="ml-2">
                          {formatDate(selectedSlot.start)} -{" "}
                          {formatDate(selectedSlot.end)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium">Date:</span>
                        <span className="ml-2">
                          {format(selectedSlot.start, "EEEE, MMMM dd, yyyy")}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <span className="font-medium">Type:</span>
                        <span
                          className={cn(
                            "ml-2 px-2 py-1 rounded text-xs font-medium",
                            selectedSlot.color === "green"
                              ? "bg-green-100 text-green-800"
                              : selectedSlot.color === "red"
                              ? "bg-red-100 text-red-800"
                              : selectedSlot.color === "orange"
                              ? "bg-orange-100 text-orange-800"
                              : selectedSlot.color === "yellow"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-blue-100 text-blue-800"
                          )}
                        >
                          {selectedSlot.color === "green"
                            ? "Available Slot"
                            : selectedSlot.color === "red"
                            ? "Booked Slot"
                            : selectedSlot.color === "orange"
                            ? "Current Schedule"
                            : selectedSlot.color === "yellow"
                            ? "Duplicate Draft Slot"
                            : "Draft Slot (Preview)"}
                        </span>
                      </div>

                      {selectedSlot.is_consumed &&
                        selectedSlot.originalData?.consumed_slots?.[0] && (
                          <>
                            <div className="flex items-center">
                              <span className="font-medium">Booked by:</span>
                              <div className="flex items-center gap-2 ml-2">
                                {selectedSlot.originalData.consumed_slots[0]
                                  .profile_img && (
                                  <Avatar className="h-6 w-6">
                                    <AvatarImage
                                      src={`${
                                        import.meta.env.VITE_API_URL
                                      }/uploads/${
                                        selectedSlot.originalData
                                          .consumed_slots[0].profile_img
                                      }`}
                                      alt={
                                        selectedSlot.originalData
                                          .consumed_slots[0].member_name
                                      }
                                    />
                                    <AvatarFallback>
                                      {selectedSlot.originalData.consumed_slots[0].member_name
                                        .charAt(0)
                                        .toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                <span>
                                  {
                                    selectedSlot.originalData.consumed_slots[0]
                                      .member_name
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Email:</span>
                              <span className="ml-2">
                                {
                                  selectedSlot.originalData.consumed_slots[0]
                                    .member_email
                                }
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Booking ID:</span>
                              <span className="ml-2">
                                {
                                  selectedSlot.originalData.consumed_slots[0]
                                    .booking_id
                                }
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="font-medium">Booked on:</span>
                              <span className="ml-2">
                                {format(
                                  new Date(
                                    selectedSlot.originalData.consumed_slots[0].created_at
                                  ),
                                  "PPP p"
                                )}
                              </span>
                            </div>
                          </>
                        )}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button>Close</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </Card>
    </div>
  );
};

export default SlotsandSchedule;
